
import React, { useState } from 'react';
import { Class } from '@/types/classTypes';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import AddScheduleDialog from './AddScheduleDialog';
import { useScheduleData } from '@/hooks/useScheduleData';
import ScheduleHeader from './ScheduleHeader';
import ScheduleTabs from './ScheduleTabs';

interface ClassScheduleManagerProps {
  organizationId: string;
  initialClassId?: string | null;
}

const ClassScheduleManager: React.FC<ClassScheduleManagerProps> = ({ 
  organizationId,
  initialClassId 
}) => {
  const [searchParams] = useSearchParams();
  const classId = searchParams.get('classId') || initialClassId || null;
  const [isAddScheduleOpen, setIsAddScheduleOpen] = useState(false);
  
  const {
    classes,
    schedules,
    enhancedClasses,
    isLoading,
    refetchSchedules
  } = useScheduleData(organizationId, classId);

  // Find the current class if a classId is provided
  const currentClass = classId ? enhancedClasses.find(c => c.id === classId) : null;

  const handleScheduleUpdated = async () => {
    await refetchSchedules();
    return Promise.resolve();
  };
  
  const handleScheduleDeleted = async () => {
    await refetchSchedules();
    return Promise.resolve();
  };
  
  const handleScheduleAdded = async () => {
    await refetchSchedules();
    toast.success('Schedule added successfully');
    return Promise.resolve();
  };

  if (isLoading) {
    return <div className="text-center py-12">Loading schedules...</div>;
  }

  return (
    <div className="space-y-4">
      <ScheduleHeader 
        currentClass={currentClass}
        onAddScheduleClick={() => setIsAddScheduleOpen(true)}
      />
      
      <ScheduleTabs
        schedules={schedules}
        classes={enhancedClasses} // Using enhanced classes with schedules included
        onScheduleUpdated={handleScheduleUpdated}
        onScheduleDeleted={handleScheduleDeleted}
        initialClassId={classId}
      />

      <AddScheduleDialog
        open={isAddScheduleOpen}
        onClose={() => setIsAddScheduleOpen(false)}
        organizationId={organizationId}
        initialClassId={classId} 
        onScheduleAdded={handleScheduleAdded}
        classId={classId || ''}
        classes={classes}
      />
    </div>
  );
};

export default ClassScheduleManager;
